import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { 
  Building2, FileText, Settings, LogOut, Plus, Trash2, Calculator,
  Crown, BarChart3, Loader2, MapPin, Bed, Bath, Square, DollarSign,
  TrendingUp, TrendingDown, Minus, Lock, ArrowRight, HelpCircle
} from "lucide-react";
import { toast } from "sonner";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from "recharts";

const ComparePage = ({ auth }) => {
  const navigate = useNavigate();
  const [properties, setProperties] = useState([
    { address: "", property_type: "single_family", bedrooms: 3, bathrooms: 2, sqft: "", current_rent: "" },
    { address: "", property_type: "single_family", bedrooms: 3, bathrooms: 2, sqft: "", current_rent: "" }
  ]);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [savedComparisons, setSavedComparisons] = useState([]);

  const currentTier = auth.user?.subscription_tier || "basic";
  const isPro = currentTier === "pro" || currentTier === "admin";

  useEffect(() => {
    if (isPro) {
      fetchSavedComparisons();
    }
  }, [isPro]);

  const fetchSavedComparisons = async () => {
    try {
      const response = await axios.get(`${API}/comparisons?limit=5`, {
        withCredentials: true
      });
      setSavedComparisons(response.data);
    } catch (error) {
      console.error("Error fetching comparisons:", error);
    }
  };

  const addProperty = () => {
    if (properties.length >= 5) {
      toast.error("Maximum 5 properties can be compared");
      return;
    }
    setProperties([...properties, { 
      address: "", property_type: "single_family", bedrooms: 3, bathrooms: 2, sqft: "", current_rent: "" 
    }]);
  };

  const removeProperty = (index) => {
    if (properties.length <= 2) {
      toast.error("At least 2 properties required");
      return;
    }
    setProperties(properties.filter((_, i) => i !== index));
  };

  const updateProperty = (index, field, value) => {
    const updated = [...properties];
    updated[index][field] = value;
    setProperties(updated);
  };

  const handleCompare = async () => {
    // Validate all addresses are filled
    const emptyAddresses = properties.filter(p => !p.address.trim());
    if (emptyAddresses.length > 0) {
      toast.error("Please fill in all property addresses");
      return;
    }

    setLoading(true);
    setResults(null);

    try {
      const response = await axios.post(`${API}/analysis/compare`, {
        properties: properties.map(p => ({
          address: p.address,
          property_type: p.property_type,
          bedrooms: parseInt(p.bedrooms),
          bathrooms: parseFloat(p.bathrooms),
          sqft: p.sqft ? parseInt(p.sqft) : null,
          current_rent: p.current_rent ? parseFloat(p.current_rent) : null
        }))
      }, { withCredentials: true });

      setResults(response.data);
      toast.success("Comparison complete!");
      fetchSavedComparisons();
    } catch (error) {
      const message = error.response?.data?.detail || "Comparison failed";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const tierBadgeColor = {
    basic: "bg-teal-100 text-teal-700",
    pro: "bg-purple-100 text-purple-700"
  };

  const getMarketIcon = (position) => {
    if (position === "above_market") return <TrendingUp className="h-4 w-4 text-emerald-500" />;
    if (position === "below_market") return <TrendingDown className="h-4 w-4 text-red-500" />;
    return <Minus className="h-4 w-4 text-slate-400" />;
  };

  const chartColors = ["#2DD4BF", "#A855F7", "#F59E0B", "#3B82F6", "#EF4444"];

  // Locked state for non-Pro users
  if (!isPro) {
    return (
      <div className="min-h-screen bg-slate-50">
        {/* Sidebar */}
        <aside className="fixed left-0 top-0 bottom-0 w-64 bg-navy-900 text-white hidden lg:block">
          <div className="p-6">
            <Link to="/" className="flex items-center space-x-2">
              <img src="https://customer-assets.emergentagent.com/job_property-analyzer-23/artifacts/1qqdoef6_Untitled_design-removebg-preview.png" alt="Rentlytix" className="h-12 w-auto" />
              
            </Link>
          </div>
          
          <nav className="px-4 space-y-1">
            <Link to="/dashboard" className="flex items-center space-x-3 px-4 py-3 rounded-lg text-slate-300 hover:bg-white/5 hover:text-white transition-colors">
              <BarChart3 className="h-5 w-5" />
              <span>Dashboard</span>
            </Link>
            <Link to="/reports" className="flex items-center space-x-3 px-4 py-3 rounded-lg text-slate-300 hover:bg-white/5 hover:text-white transition-colors">
              <FileText className="h-5 w-5" />
              <span>My Reports</span>
            </Link>
            <Link to="/calculators" className="flex items-center space-x-3 px-4 py-3 rounded-lg text-slate-300 hover:bg-white/5 hover:text-white transition-colors">
              <Calculator className="h-5 w-5" />
              <span>Deal Calculators</span>
            </Link>
            <Link to="/compare" className="flex items-center space-x-3 px-4 py-3 rounded-lg bg-white/10 text-white">
              <Calculator className="h-5 w-5" />
              <span>Compare</span>
              <Badge className="bg-purple-500 text-white text-xs ml-auto">PRO</Badge>
            </Link>
            <Link to="/subscription" className="flex items-center space-x-3 px-4 py-3 rounded-lg text-slate-300 hover:bg-white/5 hover:text-white transition-colors">
              <Crown className="h-5 w-5" />
              <span>Subscription</span>
            </Link>
            <Link to="/support" className="flex items-center space-x-3 px-4 py-3 rounded-lg text-slate-300 hover:bg-white/5 hover:text-white transition-colors">
              <HelpCircle className="h-5 w-5" />
              <span>Support</span>
            </Link>
            <Link to="/settings" className="flex items-center space-x-3 px-4 py-3 rounded-lg text-slate-300 hover:bg-white/5 hover:text-white transition-colors">
              <Settings className="h-5 w-5" />
              <span>Settings</span>
            </Link>
          </nav>

          <div className="absolute bottom-0 left-0 right-0 p-4">
            <div className="bg-white/10 rounded-lg p-4">
              <div className="flex items-center space-x-3 mb-3">
                <Avatar className="h-10 w-10">
                  <AvatarImage src={auth.user?.picture} />
                  <AvatarFallback className="bg-teal-500 text-navy-900">
                    {auth.user?.name?.charAt(0) || "U"}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{auth.user?.name}</p>
                  <Badge className={`${tierBadgeColor[currentTier]} text-xs`}>
                    {currentTier.toUpperCase()}
                  </Badge>
                </div>
              </div>
              <Button variant="ghost" size="sm" className="w-full justify-start text-slate-300 hover:text-white hover:bg-white/10" onClick={auth.logout}>
                <LogOut className="h-4 w-4 mr-2" />
                Sign Out
              </Button>
            </div>
          </div>
        </aside>

        {/* Mobile Header */}
        <header className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-white border-b border-slate-200">
          <div className="flex items-center justify-between px-4 h-16">
            <Link to="/" className="flex items-center space-x-2">
              <img src="https://customer-assets.emergentagent.com/job_property-analyzer-23/artifacts/1qqdoef6_Untitled_design-removebg-preview.png" alt="Rentlytix" className="h-12 w-auto" />
              
            </Link>
          </div>
        </header>

        {/* Locked Content */}
        <main className="lg:ml-64 pt-16 lg:pt-0">
          <div className="p-6 lg:p-8 max-w-4xl mx-auto">
            <Card className="text-center py-12">
              <CardContent>
                <div className="w-20 h-20 rounded-full bg-purple-100 flex items-center justify-center mx-auto mb-6">
                  <Lock className="h-10 w-10 text-purple-500" />
                </div>
                <h2 className="font-heading text-2xl font-bold text-navy-900 mb-2">
                  Property Comparison Calculator
                </h2>
                <p className="text-slate-600 mb-6 max-w-md mx-auto">
                  Compare up to 5 properties side-by-side to find the best investment opportunity. 
                  This feature is available exclusively for Pro subscribers.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button 
                    onClick={() => navigate("/subscription")}
                    className="bg-purple-500 hover:bg-purple-400 text-white"
                    data-testid="upgrade-to-pro-btn"
                  >
                    Upgrade to Pro - $10/mo
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={() => navigate("/dashboard")}
                  >
                    Back to Dashboard
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 bottom-0 w-64 bg-navy-900 text-white hidden lg:block">
        <div className="p-6">
          <Link to="/" className="flex items-center space-x-2">
            <img src="https://customer-assets.emergentagent.com/job_property-analyzer-23/artifacts/1qqdoef6_Untitled_design-removebg-preview.png" alt="Rentlytix" className="h-12 w-auto" />
            
          </Link>
        </div>
        
        <nav className="px-4 space-y-1">
          <Link to="/dashboard" className="flex items-center space-x-3 px-4 py-3 rounded-lg text-slate-300 hover:bg-white/5 hover:text-white transition-colors">
            <BarChart3 className="h-5 w-5" />
            <span>Dashboard</span>
          </Link>
          <Link to="/reports" className="flex items-center space-x-3 px-4 py-3 rounded-lg text-slate-300 hover:bg-white/5 hover:text-white transition-colors">
            <FileText className="h-5 w-5" />
            <span>My Reports</span>
          </Link>
          <Link to="/calculators" className="flex items-center space-x-3 px-4 py-3 rounded-lg text-slate-300 hover:bg-white/5 hover:text-white transition-colors">
            <Calculator className="h-5 w-5" />
            <span>Deal Calculators</span>
          </Link>
          <Link to="/compare" className="flex items-center space-x-3 px-4 py-3 rounded-lg bg-white/10 text-white">
            <Calculator className="h-5 w-5" />
            <span>Compare</span>
          </Link>
          <Link to="/subscription" className="flex items-center space-x-3 px-4 py-3 rounded-lg text-slate-300 hover:bg-white/5 hover:text-white transition-colors">
            <Crown className="h-5 w-5" />
            <span>Subscription</span>
          </Link>
          <Link to="/support" className="flex items-center space-x-3 px-4 py-3 rounded-lg text-slate-300 hover:bg-white/5 hover:text-white transition-colors">
            <HelpCircle className="h-5 w-5" />
            <span>Support</span>
          </Link>
          <Link to="/settings" className="flex items-center space-x-3 px-4 py-3 rounded-lg text-slate-300 hover:bg-white/5 hover:text-white transition-colors">
            <Settings className="h-5 w-5" />
            <span>Settings</span>
          </Link>
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4">
          <div className="bg-white/10 rounded-lg p-4">
            <div className="flex items-center space-x-3 mb-3">
              <Avatar className="h-10 w-10">
                <AvatarImage src={auth.user?.picture} />
                <AvatarFallback className="bg-teal-500 text-navy-900">
                  {auth.user?.name?.charAt(0) || "U"}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{auth.user?.name}</p>
                <Badge className="bg-purple-100 text-purple-700 text-xs">PRO</Badge>
              </div>
            </div>
            <Button variant="ghost" size="sm" className="w-full justify-start text-slate-300 hover:text-white hover:bg-white/10" onClick={auth.logout}>
              <LogOut className="h-4 w-4 mr-2" />
              Sign Out
            </Button>
          </div>
        </div>
      </aside>

      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-white border-b border-slate-200">
        <div className="flex items-center justify-between px-4 h-16">
          <Link to="/" className="flex items-center space-x-2">
            <img src="https://customer-assets.emergentagent.com/job_property-analyzer-23/artifacts/1qqdoef6_Untitled_design-removebg-preview.png" alt="Rentlytix" className="h-12 w-auto" />
            
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="lg:ml-64 pt-16 lg:pt-0">
        <div className="p-6 lg:p-8 max-w-6xl">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center gap-2 mb-2">
              <Calculator className="h-8 w-8 text-purple-500" />
              <h1 className="font-heading text-2xl lg:text-3xl font-bold text-navy-900">
                Property Comparison Calculator
              </h1>
            </div>
            <p className="text-slate-600">
              Compare up to 5 properties side-by-side to find the best investment
            </p>
          </div>

          {/* Property Input Cards */}
          <Card className="mb-6" data-testid="comparison-form-card">
            <CardHeader>
              <CardTitle className="font-heading flex items-center justify-between">
                <span>Properties to Compare</span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={addProperty}
                  disabled={properties.length >= 5}
                  data-testid="add-property-btn"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add Property
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {properties.map((property, index) => (
                <div 
                  key={index} 
                  className="p-4 rounded-lg bg-slate-50 border border-slate-200"
                  data-testid={`property-input-${index}`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <Badge variant="outline" className="font-semibold">Property {index + 1}</Badge>
                    {properties.length > 2 && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-red-500 hover:text-red-600 hover:bg-red-50"
                        onClick={() => removeProperty(index)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div className="md:col-span-2 lg:col-span-1">
                      <Label>Address</Label>
                      <div className="relative">
                        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <Input
                          placeholder="123 Main St, City, State"
                          value={property.address}
                          onChange={(e) => updateProperty(index, "address", e.target.value)}
                          className="pl-10"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <Label>Property Type</Label>
                      <Select 
                        value={property.property_type} 
                        onValueChange={(v) => updateProperty(index, "property_type", v)}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="single_family">Single Family</SelectItem>
                          <SelectItem value="apartment">Apartment</SelectItem>
                          <SelectItem value="condo">Condo</SelectItem>
                          <SelectItem value="townhouse">Townhouse</SelectItem>
                          <SelectItem value="duplex">Duplex</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label>Beds</Label>
                        <Select 
                          value={String(property.bedrooms)} 
                          onValueChange={(v) => updateProperty(index, "bedrooms", v)}
                        >
                          <SelectTrigger>
                            <Bed className="h-4 w-4 mr-1 text-slate-400" />
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {Array.from({length: 20}, (_, i) => i + 1).map(n => (
                              <SelectItem key={n} value={String(n)}>{n}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Baths</Label>
                        <Select 
                          value={String(property.bathrooms)} 
                          onValueChange={(v) => updateProperty(index, "bathrooms", v)}
                        >
                          <SelectTrigger>
                            <Bath className="h-4 w-4 mr-1 text-slate-400" />
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {Array.from({length: 39}, (_, i) => (i + 2) / 2).map(n => (
                              <SelectItem key={n} value={String(n)}>{n}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    
                    <div>
                      <Label>Sq Ft (optional)</Label>
                      <div className="relative">
                        <Square className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <Input
                          type="number"
                          placeholder="1500"
                          value={property.sqft}
                          onChange={(e) => updateProperty(index, "sqft", e.target.value)}
                          className="pl-10"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <Label>Current Rent (optional)</Label>
                      <div className="relative">
                        <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <Input
                          type="number"
                          placeholder="2500"
                          value={property.current_rent}
                          onChange={(e) => updateProperty(index, "current_rent", e.target.value)}
                          className="pl-10"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ))}

              <Button
                onClick={handleCompare}
                disabled={loading}
                className="w-full bg-purple-500 hover:bg-purple-400 text-white h-12"
                data-testid="compare-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                    Comparing Properties...
                  </>
                ) : (
                  <>
                    <Calculator className="h-5 w-5 mr-2" />
                    Compare Properties
                  </>
                )}
              </Button>
            </CardContent>
          </Card>

          {/* Results */}
          {results && (
            <div className="space-y-6" data-testid="comparison-results">
              {/* Summary Card */}
              <Card className="bg-purple-900 text-white">
                <CardContent className="p-6">
                  <h3 className="font-heading text-lg font-semibold mb-4">Comparison Summary</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <p className="text-purple-200 text-sm">Best Property</p>
                      <p className="font-semibold truncate">{results.summary.highest_rent_property?.split(",")[0]}</p>
                    </div>
                    <div>
                      <p className="text-purple-200 text-sm">Highest Rent</p>
                      <p className="font-data text-xl font-bold">${results.summary.highest_rent?.toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-purple-200 text-sm">Average Rent</p>
                      <p className="font-data text-xl font-bold">${results.summary.average_rent?.toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-purple-200 text-sm">Total Annual Potential</p>
                      <p className="font-data text-xl font-bold">${results.summary.total_annual_potential?.toLocaleString()}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Chart */}
              <Card>
                <CardHeader>
                  <CardTitle className="font-heading">Rent Comparison Chart</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-64 w-full">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={results.properties.map((p, i) => ({
                        name: `Property ${i + 1}`,
                        rent: p.estimated_rent,
                        address: p.address.split(",")[0]
                      }))}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey="name" fontSize={12} />
                        <YAxis fontSize={12} tickFormatter={(v) => `$${v.toLocaleString()}`} />
                        <Tooltip 
                          formatter={(value) => [`$${value.toLocaleString()}`, "Est. Rent"]}
                          labelFormatter={(label, payload) => payload[0]?.payload?.address || label}
                        />
                        <Bar dataKey="rent" radius={[4, 4, 0, 0]}>
                          {results.properties.map((_, index) => (
                            <Cell key={`cell-${index}`} fill={chartColors[index % chartColors.length]} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              {/* Property Cards */}
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {results.properties.map((prop, index) => (
                  <Card 
                    key={index} 
                    className={`border-t-4`}
                    style={{ borderTopColor: chartColors[index % chartColors.length] }}
                    data-testid={`result-card-${index}`}
                  >
                    <CardHeader className="pb-2">
                      <div className="flex items-center justify-between">
                        <Badge style={{ backgroundColor: chartColors[index % chartColors.length] }} className="text-white">
                          Property {index + 1}
                        </Badge>
                        {getMarketIcon(prop.market_position)}
                      </div>
                      <CardTitle className="font-heading text-base truncate">{prop.address}</CardTitle>
                      <CardDescription>{prop.city}, {prop.state}</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="flex justify-between items-center py-2 border-b">
                        <span className="text-slate-500 text-sm">Estimated Rent</span>
                        <span className="font-data font-bold text-lg">${prop.estimated_rent?.toLocaleString()}/mo</span>
                      </div>
                      <div className="flex justify-between items-center py-2 border-b">
                        <span className="text-slate-500 text-sm">Rent Range</span>
                        <span className="font-data text-sm">${prop.rent_range?.low?.toLocaleString()} - ${prop.rent_range?.high?.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between items-center py-2 border-b">
                        <span className="text-slate-500 text-sm">Annual Potential</span>
                        <span className="font-data font-semibold text-emerald-600">${prop.annual_income_potential?.toLocaleString()}</span>
                      </div>
                      {prop.current_rent && (
                        <div className="flex justify-between items-center py-2">
                          <span className="text-slate-500 text-sm">Current Rent</span>
                          <span className="font-data">${prop.current_rent?.toLocaleString()}/mo</span>
                        </div>
                      )}
                      <div className="flex gap-2 pt-2">
                        <Badge variant="secondary" className="text-xs">
                          <Bed className="h-3 w-3 mr-1" />
                          {prop.bedrooms} BR
                        </Badge>
                        <Badge variant="secondary" className="text-xs">
                          <Bath className="h-3 w-3 mr-1" />
                          {prop.bathrooms} BA
                        </Badge>
                        {prop.sqft && (
                          <Badge variant="secondary" className="text-xs">
                            <Square className="h-3 w-3 mr-1" />
                            {prop.sqft?.toLocaleString()}
                          </Badge>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* Recent Comparisons */}
          {savedComparisons.length > 0 && (
            <Card className="mt-8" data-testid="saved-comparisons-card">
              <CardHeader>
                <CardTitle className="font-heading">Recent Comparisons</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {savedComparisons.map((comp) => (
                    <div 
                      key={comp.comparison_id}
                      className="flex items-center justify-between p-3 rounded-lg bg-slate-50 hover:bg-slate-100"
                    >
                      <div>
                        <p className="font-medium text-navy-900">
                          {comp.properties?.length || 0} Properties Compared
                        </p>
                        <p className="text-sm text-slate-500">
                          Best: ${comp.summary?.highest_rent?.toLocaleString()}/mo • 
                          Total: ${comp.summary?.total_annual_potential?.toLocaleString()}/yr
                        </p>
                      </div>
                      <p className="text-xs text-slate-400">
                        {new Date(comp.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </main>
    </div>
  );
};

export default ComparePage;
